import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { findAirport } from "@/lib/airports";
import { formatDisplayPrice } from "@/lib/price-display";
import { fetchRouteStats, pairKey } from "@/lib/price-stats.functions";
import { classifyPrice, type PriceBadge } from "@/lib/price-badge";

export type GlobalDeal = {
  from: string;
  to: string;
  fromCity: string;
  toCity: string;
  toCountry: string;
  price: string;
  priceNumeric: number;
  /** Data lotu (YYYY-MM-DD), dla której obowiązuje cena z Aviasales. */
  departDate?: string;
  badge?: PriceBadge | null;
};

const InputSchema = z.object({
  limit: z.number().int().min(1).max(200).optional().default(50),
  /** Opcjonalny filtr lotniska wylotu. Akceptuje IATA pojedynczego lotniska
   *  (WAW, KRK), kod metro (WAR → WAW+WMI) lub kod kraju (XPL → wszystkie
   *  polskie lotniska). Wielkość liter bez znaczenia. */
  origin: z.string().min(2).max(4).optional(),
});

/**
 * Globalne okazje: najtańsze loty ze WSZYSTKICH polskich huby × wszystkie kierunki,
 * dedup per (from_city, to_city) — jedno najlepsze ofertowanie per para miast.
 * Czytane z cache `route_prices` (odświeżane cronem).
 */
export const getGlobalDeals = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => InputSchema.parse(input))
  .handler(async ({ data }): Promise<GlobalDeal[]> => {
    try {
      const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
      const key =
        process.env.SUPABASE_SERVICE_ROLE_KEY ??
        process.env.SUPABASE_PUBLISHABLE_KEY ??
        process.env.SUPABASE_ANON_KEY ??
        process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (!url || !key) return [];

      const sb = createClient(url, key, {
        auth: { persistSession: false, autoRefreshToken: false },
      });

      // Jeśli podano `origin`, rozwijamy metro/kraj do listy konkretnych
      // IATA i ograniczamy zapytanie po stronie bazy — szybciej niż pull 500
      // i filter w pamięci.
      const originFilter = (() => {
        if (!data.origin) return null;
        const a = findAirport(data.origin);
        if (!a) return [data.origin.toUpperCase()];
        if ((a.isMetro || a.isCountry) && a.members?.length) return a.members.map((m) => m.toUpperCase());
        return [a.code.toUpperCase()];
      })();

      let q = sb
        .from("route_prices")
        .select("from_iata,to_iata,display_price,depart_date")
        .order("display_price", { ascending: true })
        .limit(originFilter ? 1000 : 500);
      if (originFilter) q = q.in("from_iata", originFilter);

      const { data: rows } = await q;
      if (!rows || rows.length === 0) return [];

      const bestPerPair = new Map<string, GlobalDeal>();
      for (const r of rows) {
        if (typeof r.display_price !== "number" || r.display_price <= 0) continue;
        const af = findAirport(r.from_iata);
        const at = findAirport(r.to_iata);
        if (!af || !at) continue;
        // Z Polski + wybrane sąsiednie huby przydatne dla polskich pasażerów (Berlin).
        // Jeśli `originFilter` jest aktywny, to lista IATA już zawęża wynik,
        // więc filtr „Polska/allowlist” pomijamy (user mógł zażyczyć sobie BER).
        if (!originFilter) {
          const FOREIGN_ORIGIN_ALLOWLIST = new Set(["BER"]);
          if (af.country !== "Polska" && !FOREIGN_ORIGIN_ALLOWLIST.has(r.from_iata)) continue;
        }
        const k = `${af.city}|${at.city}`;
        const existing = bestPerPair.get(k);
        if (existing && existing.priceNumeric <= r.display_price) continue;
        bestPerPair.set(k, {
          from: r.from_iata,
          to: r.to_iata,
          fromCity: af.city,
          toCity: at.city,
          toCountry: at.country,
          priceNumeric: r.display_price,
          departDate: (r as { depart_date?: string | null }).depart_date ?? undefined,
          price: formatDisplayPrice(r.display_price),
        });
      }

      const deals = [...bestPerPair.values()]
        .sort((a, b) => a.priceNumeric - b.priceNumeric)
        .slice(0, data.limit);

      try {
        const stats = await fetchRouteStats(
          deals.map((d) => ({ from: d.from, to: d.to })),
          30,
        );
        for (const d of deals) {
          d.badge = classifyPrice(d.priceNumeric, stats[pairKey(d.from, d.to)]);
        }
      } catch {
        // best-effort
      }

      return deals;
    } catch {
      return [];
    }
  });
